import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { useFlags } from "@/utils/launchDarkly";
import { Box, VStack } from "@whoosh/design-system/src/components/primitive";

import { BookingPromoCode } from "./BookingPromoCode";
import { BookingPromoCodeSectionQuery } from "./__generated__/BookingPromoCodeSectionQuery.graphql";

type Props = {
  bookingId: string;
  canApply: boolean;
  isEditable: boolean;
  withDividers: boolean;
};

export const BookingPromoCodeSectionQueryString = graphql`
  query BookingPromoCodeSectionQuery($bookingId: ID!) {
    node(id: $bookingId) {
      ... on Booking {
        id
        ...promoCodeAnalytics_booking
        appliedPromoCode {
          id
          code
          discountType
          discountAmount
          calculationType
        }
      }
    }
  }
`;

export const BookingPromoCodeSection = ({
  bookingId,
  canApply,
  isEditable,
  withDividers,
}: Props) => {
  const { couponCodes } = useFlags();
  const couponCodesEnabled = couponCodes === true;
  const [queryRef, loadQuery, disposeQuery] =
    useQueryLoader<BookingPromoCodeSectionQuery>(
      BookingPromoCodeSectionQueryString
    );

  useEffect(() => {
    if (!couponCodesEnabled) {
      disposeQuery();
      return;
    }

    loadQuery({ bookingId }, { fetchPolicy: "store-and-network" });
  }, [bookingId, couponCodesEnabled, disposeQuery, loadQuery]);

  if (!couponCodesEnabled || !queryRef) {
    return null;
  }

  return (
    <ErrorBoundary fallbackRender={() => null} resetKeys={[queryRef]}>
      <Suspense fallback={null}>
        <BookingPromoCodeSectionContent
          canApply={canApply}
          isEditable={isEditable}
          queryRef={queryRef}
          withDividers={withDividers}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

type ContentProps = Omit<Props, "bookingId"> & {
  queryRef: PreloadedQuery<BookingPromoCodeSectionQuery>;
};

const BookingPromoCodeSectionContent = ({
  canApply,
  isEditable,
  queryRef,
  withDividers,
}: ContentProps) => {
  const data = usePreloadedQuery(BookingPromoCodeSectionQueryString, queryRef);
  const booking = data.node;

  if (!booking?.id || (!booking.appliedPromoCode && !canApply)) {
    return null;
  }

  const promoCode = (
    <BookingPromoCode
      bookingId={booking.id}
      bookingAnalyticsRef={booking}
      appliedCode={booking.appliedPromoCode?.code}
      calculationType={booking.appliedPromoCode?.calculationType}
      discountAmount={booking.appliedPromoCode?.discountAmount}
      discountType={booking.appliedPromoCode?.discountType}
      isEditable={booking.appliedPromoCode ? isEditable : canApply}
    />
  );

  if (!withDividers) {
    return promoCode;
  }

  return (
    <VStack gap={4}>
      <Box
        height={1}
        backgroundColor="outline-default"
        width="100%"
        style={{ marginTop: 1.5, marginBottom: 1.5 }}
      />
      {promoCode}
      <Box
        height={1}
        backgroundColor="outline-default"
        width="100%"
        style={{ marginTop: 1.5, marginBottom: 1.5 }}
      />
    </VStack>
  );
};
